import React, { useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import { Button } from "@/shared/components/ui/button";
import { RefreshCw, Eye, EyeOff, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { generatePassword } from "@/shared/utils/utilities";

export default function GeneralTab({
  merchant,
  t,
  engines = [],
  resellers = [],
  role = "admin",
}) {
  const [showPassword, setShowPassword] = useState(false);
  const { register, control, setValue, watch, formState: { errors } } = useFormContext();

  const parentType = watch("parent_type");

  const handleGeneratePassword = () => {
    const newPass = generatePassword();
    setValue("password", newPass, { shouldValidate: true });
    navigator.clipboard.writeText(newPass);
    toast.success(
      t("passwordGeneratedAndCopied") ||
        "Password generated and copied to clipboard",
    );
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>{t("merchantName")} *</Label>
          <Input
            {...register("name", { required: t("nameRequired") || "Name is required" })}
            placeholder={t("merchantNamePlaceholder")}
            className={errors.name ? "border-red-500 focus:ring-red-500/20" : ""}
          />
          {errors.name && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {errors.name.message}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <Label>{t("merchantCode")} *</Label>
          <Input
            {...register("code", { required: t("codeRequired") || "Code is required" })}
            onChange={(e) => setValue("code", e.target.value.toUpperCase(), { shouldValidate: true })}
            placeholder={t("merchantCodePlaceholder")}
            className={errors.code ? "border-red-500 focus:ring-red-500/20" : ""}
          />
          {errors.code && (
            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {errors.code.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <Label>{t("emailLabel")} *</Label>
        <Input
          type="email"
          {...register("email", { 
            required: t("emailRequired") || "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: t("invalidEmail") || "Invalid email format"
            }
          })}
          placeholder={t("emailPlaceholder")}
          disabled={!!merchant}
          autoComplete="off"
          className={errors.email ? "border-red-500 focus:ring-red-500/20" : ""}
        />
        {errors.email && (
          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> {errors.email.message}
          </p>
        )}
        {merchant && (
          <p className="text-xs text-slate-500">{t("emailCannotBeChanged")}</p>
        )}
      </div>

      <div className="space-y-1">
        <Label>
          {merchant ? t("changePassword") : t("password") + " *"}
        </Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Input
              type={showPassword ? "text" : "password"}
              {...register("password", {
                required: merchant ? false : (t("passwordRequired") || "Password is required"),
                minLength: {
                  value: 8,
                  message: t("passwordMinLength") || "Password must be at least 8 characters"
                },
                validate: {
                  hasUppercase: (value) => !value || /[A-Z]/.test(value) || (t("passwordUppercaseRequired") || "Password must contain at least one uppercase letter"),
                  hasLowercase: (value) => !value || /[a-z]/.test(value) || (t("passwordLowercaseRequired") || "Password must contain at least one lowercase letter"),
                  hasSpecialChar: (value) => !value || /[!@#$%^&*(),.?":{}|<>]/.test(value) || (t("passwordSpecialCharRequired") || "Password must contain at least one special character"),
                }
              })}
              placeholder={merchant ? t("leaveBlankToKeepPassword") : t("passwordPlaceholder")}
              autoComplete="new-password"
              className={`pr-10 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all rounded-lg ${errors.password ? "border-red-500 focus:ring-red-500/20" : ""}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-slate-400 hover:text-slate-600"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-10 px-3 flex items-center gap-2 group border border-blue-100 hover:border-blue-600 hover:bg-blue-600 hover:text-white text-blue-600 font-medium transition-all duration-300 rounded-lg shadow-sm w-full sm:w-auto flex-shrink-0"
            onClick={handleGeneratePassword}
          >
            <RefreshCw className="h-4 w-4 group-hover:rotate-180 transition-transform duration-500" />
            <span className="text-xs sm:text-sm">
              {t("generatePassword")}
            </span>
          </Button>
        </div>
        {errors.password && (
          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> {errors.password.message}
          </p>
        )}
        {merchant && (
          <p className="text-xs text-slate-500 italic px-1">
            {t("changePasswordDescription")}
          </p>
        )}
      </div>

      {merchant && (
        <div className="space-y-1">
          <Label>{t("status")}</Label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">{t("active")}</SelectItem>
                  <SelectItem value="INACTIVE">{t("inactive")}</SelectItem>
                  <SelectItem value="SUSPENDED">{t("suspended")}</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
      )}

      {role === "admin" && (
        <div className="space-y-1">
          <Label>{t("parentType")}</Label>
          <Controller
            name="parent_type"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(v) => {
                  field.onChange(v);
                  setValue("engine_id", "");
                  setValue("reseller_id", "");
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">{t("directAdmin")}</SelectItem>
                  <SelectItem value="engine">{t("engine")}</SelectItem>
                  <SelectItem value="reseller">{t("reseller")}</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <p className="text-xs text-slate-500">{t("whoManagesMerchant")}</p>
        </div>
      )}

      {role === "admin" && parentType === "engine" && (
        <div className="space-y-1">
          <Label>{t("engine")}</Label>
          <Controller
            name="engine_id"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("selectEngine")} />
                </SelectTrigger>
                <SelectContent>
                  {engines.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} {e.email && `(${e.email})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      )}

      {role === "admin" && parentType === "reseller" && (
        <div className="space-y-1">
          <Label>{t("reseller")}</Label>
          <Controller
            name="reseller_id"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("selectReseller")} />
                </SelectTrigger>
                <SelectContent>
                  {resellers.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name} {r.email && `(${r.email})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      )}

      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
        <div>
          <Label>{t("enableDeposits")}</Label>
          <p className="text-sm text-slate-500">
            {t("allowDepositTransactions")}
          </p>
        </div>
        <Controller
          name="depositEnabled"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
        <div>
          <Label>{t("enableWithdrawals")}</Label>
          <p className="text-sm text-slate-500">
            {t("allowWithdrawalTransactions")}
          </p>
        </div>
        <Controller
          name="withdrawalEnabled"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="space-y-1">
        <Label>{t("notes")}</Label>
        <Textarea
          {...register("notes")}
          placeholder={t("internalNotes")}
          rows={3}
        />
      </div>
    </div>
  );
}
